import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { CButton, CFormCheck, CFormInput, CFormLabel } from "@coreui/react";
import isEqual from "lodash-es/isEqual";

import type { RunFilters } from "@src/types/filters";

/**
 * Local form state type - separate from RunFilters because:
 * - HTML inputs always use strings for value prop (even type="number")
 * - Form allows empty strings, business logic uses null
 * - Conversion happens in handleApply() when emitting to parent
 */
interface LocalFilterState {
  active: boolean;
  name: string;
  id: string;
  jobId: string;
}

interface Props {
  appliedFilters: RunFilters;
  onApplyFilters: (filters: RunFilters) => void;
  onClearFilters: () => void;
  disabled?: boolean;
}

// Convert applied filters to local form state
const getDraftState = (applied: RunFilters): LocalFilterState => ({
  active: applied.active,
  name: applied.name || "",
  id: applied.id?.toString() || "",
  jobId: applied.jobId?.toString() || "",
});

export const FilterRunControls: React.FC<Props> = ({
  appliedFilters,
  onApplyFilters,
  onClearFilters,
  disabled = false,
}) => {
  const location = useLocation();
  const isExecution = location.pathname.includes("execution");
  const entityLabel = isExecution ? "Execution" : "Backtest";

  const [draftFilters, setDraftFilters] = useState<LocalFilterState>(() =>
    getDraftState(appliedFilters),
  );

  // Update draft state when applied filters change (e.g., page navigation)
  useEffect(() => {
    setDraftFilters(getDraftState(appliedFilters));
  }, [appliedFilters]);

  // Memoize applied draft and compare for dirty indicator
  const appliedDraft = useMemo(() => getDraftState(appliedFilters), [appliedFilters]);
  const isDirty = !isEqual(draftFilters, appliedDraft);

  const handleApply = () => {
    // Validate numeric inputs: trim, ensure integer digits only, enforce >= 1
    const parsePositiveInt = (value: string): number | null => {
      if (!value) return null;
      const trimmed = value.trim();
      if (trimmed === "") return null;
      // Accept only digits (no sign, no decimals)
      if (!/^\d+$/.test(trimmed)) return null;
      const parsed = parseInt(trimmed, 10);
      if (!Number.isFinite(parsed) || parsed < 1) return null;
      return parsed;
    };

    const validatedId = parsePositiveInt(draftFilters.id);
    const validatedJobId = parsePositiveInt(draftFilters.jobId);

    const newFilters: RunFilters = {
      active: draftFilters.active,
      name: draftFilters.name ? draftFilters.name.trim() || null : null,
      id: validatedId,
      jobId: validatedJobId,
    };
    onApplyFilters(newFilters);
  };

  const handleClear = () => {
    onClearFilters();
  };

  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newActive = e.target.checked;
    setDraftFilters((prev) => ({ ...prev, active: newActive }));

    // Apply immediately - toggling active shouldn't require clicking "Filter"
    const validatedId =
      draftFilters.id.trim() && /^\d+$/.test(draftFilters.id.trim())
        ? parseInt(draftFilters.id.trim(), 10)
        : null;
    const validatedJobId =
      draftFilters.jobId.trim() && /^\d+$/.test(draftFilters.jobId.trim())
        ? parseInt(draftFilters.jobId.trim(), 10)
        : null;

    onApplyFilters({
      active: newActive,
      name: draftFilters.name ? draftFilters.name.trim() || null : null,
      id: validatedId,
      jobId: validatedJobId,
    });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDraftFilters((prev) => ({ ...prev, name: e.target.value }));
  };

  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDraftFilters((prev) => ({ ...prev, id: e.target.value }));
  };

  const handleJobIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDraftFilters((prev) => ({ ...prev, jobId: e.target.value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isDirty && !disabled) {
      handleApply();
    }
  };

  return (
    <div className="FilterRunControls mb-3">
      <div className="d-flex flex-wrap gap-3 align-items-center">
        {/* Active Checkbox - only enabled for executions */}
        <div className="d-flex align-items-center">
          <CFormCheck
            id="activeCheckbox"
            checked={draftFilters.active}
            onChange={handleActiveChange}
            label="Active"
            disabled={disabled || !isExecution}
          />
        </div>

        {/* ID Input */}
        <div className="d-flex align-items-center">
          <CFormLabel htmlFor="idInput" className="mb-0 me-2">
            ID:
          </CFormLabel>
          <CFormInput
            type="number"
            id="idInput"
            size="sm"
            min="1"
            value={draftFilters.id}
            onChange={handleIdChange}
            onKeyDown={handleKeyDown}
            placeholder={`${entityLabel} ID`}
            style={{ width: "90px" }}
            disabled={disabled}
          />
        </div>

        {/* Job ID Input */}
        <div className="d-flex align-items-center">
          <CFormLabel htmlFor="jobIdInput" className="mb-0 me-2">
            Job ID:
          </CFormLabel>
          <CFormInput
            type="number"
            id="jobIdInput"
            size="sm"
            min="1"
            value={draftFilters.jobId}
            onChange={handleJobIdChange}
            onKeyDown={handleKeyDown}
            placeholder="Job ID"
            style={{ width: "90px" }}
            disabled={disabled}
          />
        </div>

        {/* Name Input */}
        <div className="d-flex align-items-center">
          <CFormLabel htmlFor="nameInput" className="mb-0 me-2">
            Name:
          </CFormLabel>
          <CFormInput
            type="text"
            id="nameInput"
            size="sm"
            value={draftFilters.name}
            onChange={handleNameChange}
            onKeyDown={handleKeyDown}
            placeholder="Filter by name..."
            style={{ width: "160px" }}
            disabled={disabled}
          />
        </div>

        <CButton
          size="sm"
          color={isDirty ? "primary" : "secondary"}
          variant={isDirty ? undefined : "outline"}
          onClick={handleApply}
          disabled={disabled || !isDirty}
        >
          Filter
        </CButton>

        <CButton
          size="sm"
          color="secondary"
          variant="outline"
          className="ms-auto"
          onClick={handleClear}
          disabled={disabled}
        >
          Clear
        </CButton>
      </div>
    </div>
  );
};
